import { useMutation, useQuery } from '@tanstack/react-query';
import { createForecaster, deleteForecaster, getForecasters, updateForecaster } from '../../services/forecasters';
import { QueryKey } from '../../enums/QueryKey';
import { useDispatchErrorNotification } from '../useDispatchErrorNotification';
import { useDispatchSuccessfullySavedRecord } from '../useDispatchSuccessfullySavedRecord';
import { queryClient } from '../../services/queryClient';
import type { UseMutationOptions } from '@tanstack/react-query/src/types';
import { Forecaster, ForecasterEditForm, ForecasterForm } from '../../types/Forecaster.interface';
import { useDispatchSuccessfullyDeletedRecord } from '../useDispatchSuccessNotification';

export function useForecasters() {
    const { data, ...rest } = useQuery({
        queryKey: [QueryKey.Forecasters],
        queryFn: getForecasters,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
    });

    return { data: data || [], ...rest };
}

export function useCreateForecaster({ onSettled }: UseMutationOptions<Forecaster, Error, ForecasterForm>) {
    const { dispatchErrorNotification } = useDispatchErrorNotification();
    const { dispatchSuccessfullySavedRecord } = useDispatchSuccessfullySavedRecord();

    return useMutation<Forecaster, Error, ForecasterForm>({
        mutationFn: createForecaster,
        onSuccess: () => {
            queryClient.invalidateQueries([QueryKey.Forecasters]);
            dispatchSuccessfullySavedRecord('Forecaster');
        },
        onError: (error) => {
            dispatchErrorNotification({ message: error.message });
        },
        onSettled,
    });
}

export function useUpdateForecaster({ onSettled }: UseMutationOptions<Forecaster, Error, ForecasterEditForm>) {
    const { dispatchErrorNotification } = useDispatchErrorNotification();
    const { dispatchSuccessfullySavedRecord } = useDispatchSuccessfullySavedRecord();

    return useMutation<Forecaster, Error, ForecasterEditForm>({
        mutationFn: updateForecaster,
        onSuccess: () => {
            queryClient.invalidateQueries([QueryKey.Forecasters]);
            dispatchSuccessfullySavedRecord('Forecaster');
        },
        onError: (error) => {
            dispatchErrorNotification({ message: error.message });
        },
        onSettled,
    });
}

export function useDeleteForecaster({ onSettled }: UseMutationOptions<Forecaster, Error, number>) {
    const { dispatchErrorNotification } = useDispatchErrorNotification();
    const { dispatchSuccessfullyDeletedRecord } = useDispatchSuccessfullyDeletedRecord();

    return useMutation<Forecaster, Error, number>({
        mutationFn: deleteForecaster,
        onSuccess: () => {
            queryClient.invalidateQueries([QueryKey.Forecasters]);
            dispatchSuccessfullyDeletedRecord('Forecaster');
        },
        onError: (error) => {
            dispatchErrorNotification({ message: error.message });
        },
        onSettled,
    });
}
